import { cn } from '@sjuni/ui';
import { TextSelection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import {
  Bold,
  Code,
  FileText,
  ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  RemoveFormatting,
  Table,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentLibraryDialog } from '@/components/documents/DocumentLibraryDialog';
import { MediaLibraryDialog } from '@/components/media/MediaPicker';
import { valueFor } from '@/lib/forms/translatable';

import { documentLinkAttributes } from './richTextExtensions';

/**
 * docs/05-ADMIN-DASHBOARD.md §4.2's toolbar.
 *
 * "bold · italic · underline · H2/H3/H4 · bullet list · ordered list ·
 * blockquote · link · table · image (from media library) · document link (from
 * library) · horizontal rule · undo/redo · **clear formatting** · source view
 * (Super Admin only)"
 *
 * The image and document-link buttons open the two libraries. Both insert a
 * *reference* rather than a URL: `data-media-id` and `data-document-id` are what
 * `MediaUsage` and `DocumentReferences` read, so deleting a picture or a PDF that
 * an article body uses is refused with a 409 naming the article. An editor pasting
 * a URL by hand still gets a working link — it is simply not tracked, which is why
 * these buttons exist rather than a text field.
 *
 * Every control is a `button` with a pressed state rather than a styled div,
 * because the state *is* the information: an editor needs to know whether the
 * cursor is already inside a list before pressing the list button.
 */
export interface RichTextToolbarProps {
  editor: Editor;
  /** §4.2 — "source view (Super Admin only)". */
  showSource: boolean;
  onToggleSource: () => void;
  canViewSource: boolean;
  disabled?: boolean;
}

export function RichTextToolbar({
  editor,
  showSource,
  onToggleSource,
  canViewSource,
  disabled,
}: RichTextToolbarProps) {
  const { t } = useTranslation();

  /*
   * One piece of state for two dialogs rather than two booleans: they are mutually
   * exclusive, and a pair of booleans is a pair that can both be true.
   */
  const [picker, setPicker] = useState<'image' | 'document' | null>(null);

  return (
    <div
      role="toolbar"
      aria-label={t('editor.richText.toolbar')}
      aria-orientation="horizontal"
      className="flex flex-wrap items-center gap-1 border-b border-subtle bg-surface-alt p-2"
    >
      <ToolbarButton
        label={t('editor.richText.bold')}
        active={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={16} strokeWidth={2} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.italic')}
        active={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={16} strokeWidth={2} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.underline')}
        active={editor.isActive('underline')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={16} strokeWidth={2} aria-hidden="true" />
      </ToolbarButton>

      <Divider />

      {/*
        H2 to H4. The page's own H1 belongs to the record's title field, not to
        its body — an editor who can add a second H1 will, and §7.3 of the
        frontend spec has exactly one per page.
      */}
      {([2, 3, 4] as const).map((level) => (
        <ToolbarButton
          key={level}
          label={t('editor.richText.heading', { level })}
          active={editor.isActive('heading', { level })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
        >
          {/* Decorative — the button's name is the translated label above.
              `H2` is not a string to translate; it is the shape of the
              control, and Georgian uses the same abbreviation. */}
          <span aria-hidden="true" className="text-caption font-extrabold">
            {`H${level}`}
          </span>
        </ToolbarButton>
      ))}

      <Divider />

      <ToolbarButton
        label={t('editor.richText.bulletList')}
        active={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.orderedList')}
        active={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.blockquote')}
        active={editor.isActive('blockquote')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label={t('editor.richText.link')}
        active={editor.isActive('link')}
        disabled={disabled}
        onClick={() => promptForLink(editor, t)}
      >
        <Link2 size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.image')}
        disabled={disabled}
        onClick={() => setPicker('image')}
      >
        <ImageIcon size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.document')}
        disabled={disabled}
        onClick={() => setPicker('document')}
      >
        <FileText size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.table')}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
      >
        <Table size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.horizontalRule')}
        disabled={disabled}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        label={t('editor.richText.clearFormatting')}
        disabled={disabled}
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        <RemoveFormatting size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.undo')}
        disabled={disabled || !editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      <ToolbarButton
        label={t('editor.richText.redo')}
        disabled={disabled || !editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>

      {/* §4.2 — Super Admin only. Raw HTML is how somebody pastes in an
          `<iframe>`; the sanitiser refuses it on save either way, but the
          control does not need to be in front of everyone. */}
      {canViewSource ? (
        <ToolbarButton
          label={t('editor.richText.source')}
          active={showSource}
          disabled={disabled}
          onClick={onToggleSource}
          className="ms-auto"
        >
          <Code size={16} strokeWidth={1.75} aria-hidden="true" />
        </ToolbarButton>
      ) : null}

      {picker === 'image' ? (
        <MediaLibraryDialog
          onClose={() => setPicker(null)}
          onPick={(media) => {
            /*
             * The `card` conversion, not the original: a body image is read at
             * about 700px on a desktop and less on a phone, and serving a 4 MB
             * original to do it is the difference this library exists to make. The
             * original stays reachable — it is the same media row.
             */
            /*
             * One `insertContent` rather than `setImage` then
             * `updateAttributes`: the second form depends on the selection
             * landing on the new node, and an image inserted with no
             * `data-media-id` is exactly the untracked reference this is for.
             */
            editor
              .chain()
              .focus()
              .insertContent({
                type: 'image',
                attrs: {
                  src: media.urls.card === '' ? media.urls.original : media.urls.card,
                  // The library's own description. An undescribed image cannot be
                  // attached elsewhere and should not arrive here without one.
                  alt: media.alt.ka ?? media.alt.en ?? '',
                  mediaId: media.id,
                },
              })
              .run();

            setPicker(null);
          }}
        />
      ) : null}

      {picker === 'document' ? (
        <DocumentLibraryDialog
          onClose={() => setPicker(null)}
          onPick={(document) => {
            const title = valueFor(document.title, 'ka') || document.file_name;

            /*
             * Inserted as the document's title when nothing is selected, and as a
             * link around the selection when there is one — which is what every
             * editor expects of a link button.
             */
            const attributes = documentLinkAttributes(document.id);

            /*
             * Whether there is *text* to wrap, which is not the same as "the
             * selection is not empty": with an image selected the selection is a
             * NodeSelection, `empty` is false, and `setLink` on it applies a mark to
             * a node that cannot carry one — so the button did nothing at all, which
             * is how this was found.
             */
            const { selection } = editor.state;
            const wrapsText = selection instanceof TextSelection && !selection.empty;

            if (!wrapsText) {
              editor
                .chain()
                .focus()
                .insertContent({
                  type: 'text',
                  text: title,
                  marks: [{ type: 'link', attrs: attributes }],
                })
                .run();
            } else {
              editor.chain().focus().setLink(attributes).run();
            }

            setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
}

function Divider() {
  return <span aria-hidden="true" className="bg-strong mx-1 h-5 w-px" />;
}

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}

function ToolbarButton({
  label,
  active = false,
  disabled,
  onClick,
  className,
  children,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      // The icon is decorative, so the button's name comes from here — and
      // `aria-pressed` carries the state a highlight would only show.
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md transition-colors duration-fast',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
        'disabled:pointer-events-none disabled:opacity-40',
        active ? 'bg-navy-600 text-white' : 'text-secondary hover:bg-surface hover:text-primary',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * A prompt rather than a dialog, for now.
 *
 * §4.2 asks for a link button and does not describe its UI; the record picker
 * that would make this good — "link to a news article" rather than "paste a
 * URL" — belongs with the modules that have records to pick. `window.prompt`
 * is honest about being the simple version, and it does not pretend to
 * validate: the server's sanitiser decides what survives.
 */
function promptForLink(editor: Editor, t: (key: string) => string): void {
  const existing = String(editor.getAttributes('link').href ?? '');
  const href = window.prompt(t('editor.richText.linkPrompt'), existing);

  if (href === null) {
    return;
  }

  if (href.trim() === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();

    return;
  }

  editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
}
